import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import Sidebar from "../components/layout/Sidebar";
import Header from "../components/layout/Header";
import DashboardCard from "../components/common/DashboardCard";
import ScoreChart from "../components/common/ScoreChart";
import { useTranslation } from "react-i18next";
import { BASE_URL } from "../config";

const DashboardPage = () => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user?._id || !user?.token) return;

      setLoading(true);

      const res = await fetch(`${BASE_URL}/user/dash/${user._id}`, {
        headers: { "x-access-token": user.token },
      });

      if (!res.ok) throw new Error("Unauthorized");

      const json = await res.json();
      setData(json?.data || null);
    } catch (e) {
      localStorage.setItem("user", null);
      window.location.href = "/";
    } finally {
      setLoading(false);
    }
  };

  const sampleExams = []; // ✅ فقط برای fallback، اگر لازم داری

  const isRtl = i18n.language.includes("fa");

  const mainContentClass = `flex-1 flex flex-col px-2 pb-5 lg:px-0 lg:pb-0 ${
    isRtl ? "md:mr-64" : "md:ml-64"
  }`;
  const mainSectionClass = `flex-1 overflow-y-auto p-4 md:p-4 rounded-[16px] bg-gray-100 ${
    isRtl ? "lg:me-3 lg:mb-3" : "lg:ms-3 lg:mb-3"
  }`;

  // ✅ Skeleton ساده
  const Skeleton = ({ className = "" }) => (
    <div className={`animate-pulse bg-gray-200 rounded-lg ${className}`} />
  );

  return (
    <div className="flex h-screen">
      <Helmet>
        <title>{t("dashboard.page_title")}</title>
      </Helmet>

      <Sidebar />
      <div className={mainContentClass}>
        <Header />

        <main className={mainSectionClass}>
          {/* ✅ Loading Skeleton */}
          {loading ? (
            <>
              {/* Cards Skeleton */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                <Skeleton className="h-[120px]" />
                <Skeleton className="h-[120px]" />
                <Skeleton className="h-[120px]" />
              </div>

              <div className="grid lg:grid-cols-3 gap-4">
                <Skeleton className="lg:col-span-2 h-[320px]" />
                <Skeleton className="h-[320px]" />
              </div>
            </>
          ) : (
            <>
              {/* کارت‌های اطلاعاتی */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                <DashboardCard
                  title={t("dashboard.subscription_status")}
                  value={
                    data?.subscriptions?.activeCount > 0
                      ? `${t("dashboard.active")} (${
                          data.subscriptions.activeCount
                        })`
                      : t("dashboard.inactive")
                  }
                  icon="user"
                  color={data?.subscriptions?.activeCount > 0 ? "green" : "red"}
                  to={"/subscription"}
                />

                <DashboardCard
                  title={t("dashboard.results_count", "تعداد نتایج")}
                  value={data?.resultsCount || 0}
                  icon="pen"
                  color="blue"
                  to={"/result"}
                />

                <DashboardCard
                  title={t("dashboard.overall_progress")}
                  value={`${data?.progressDelta || 0}%`}
                  icon="chart"
                  color="purple"
                />
              </div>

              {/* لیست آخرین آزمون‌ها */}
              <div className="grid lg:grid-cols-3 gap-4">
                <span className="lg:col-span-2">
                  <ScoreChart exams={data?.latestExams || []} />
                </span>

                <section className="bg-white rounded-xl p-4 shadow-sm">
                  <h2 className="text-lg font-bold text-gray-700 mb-3">
                    {t("dashboard.latest_exams")}
                  </h2>

                  {data?.latestExams?.length > 0 ? (
                    <ul className="text-sm divide-y divide-gray-100">
                      {data.latestExams.map((exam) => (
                        <li
                          key={exam.id}
                          className="py-3 flex justify-between items-center px-2 hover:bg-gray-50 transition-colors duration-150 rounded-md"
                        >
                          <span className="font-medium text-gray-800">
                            {exam.title}
                          </span>
                          <span className="text-gray-500 text-xs bg-gray-100 px-2 py-1 rounded-full">
                            {exam.date}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-500 text-sm">
                      {t("dashboard.no_exams_found")}
                    </p>
                  )}
                </section>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default DashboardPage;
